import { HttpClient } from '@angular/common/http';
import { ApiService } from 'src/app/services/api.service';

/**
 * Voyage Radar's SAVED REFRESH ARCHIVE — the four questions the shared
 * snapshot module asks about replayable captures, answered in VR's dialect.
 *
 * WHY PLAIN FUNCTIONS AND NOT A SHARED INTERFACE (decision D8). Snapshot
 * playback over a server-side archive is a Voyage Radar capability: AISstream
 * replays from its own SSE buffer and has no archive at all, and no other
 * provider is known to have one. Declaring an `ArchivedCapture` vocabulary in
 * `ais/shared/contracts/` would be inventing a shape for a variation that may
 * never arrive — the same trap D6 and `VesselSearchSource` were deferred to
 * avoid. So the VR knowledge lives here, and the shared module receives these
 * as ordinary injected callbacks, exactly as `TargetController` receives
 * `ownScene` (phase 2) for the identical problem.
 *
 * The record stays `any` on both sides on purpose: it is VR's refresh-history
 * row, the shared module never inspects it, and giving it a type would be the
 * abstraction this decision declined to build. If a second provider ever grows
 * an archive, THAT is when these four become an interface — designed against
 * two real implementers instead of one guess.
 */

/** Every archived capture on the open sensor, oldest-first (storage order). */
export function vrArchive(sensor: any): any[] {
  return Array.isArray(sensor?.refreshHistory) ? sensor.refreshHistory : [];
}

/**
 * When a refresh-history record was captured, or null if it cannot be a frame.
 *
 * The window test and the playable-count are the same question asked over
 * different spans, so the per-record verdict lives in one place: a gate that
 * disagreed with the load about what counts would hide the control over an
 * archive the replay could play, or offer it over one it could not.
 *
 * A POI refresh stores the positionless nearby LIST, not a frame — it cannot
 * be played back, only re-opened from Refresh History.
 */
export function vrCaptureTime(h: any): number | null {
  if (h?.area?.type === 'Point') return null;
  const t = new Date(h?.name).getTime();
  return isNaN(t) ? null : t;
}

/**
 * The label a capture carries back to the panel — VR stores the ISO stamp in
 * `name`, and the frame is titled with it.
 */
export function vrCaptureLabel(h: any): string {
  return h?.name;
}

/** The stored id of a capture, or null — what marks the row being played. */
export function vrCaptureId(h: any): string | null {
  return h?.refreshId ?? null;
}

/**
 * A fingerprint that changes whenever the archive does.
 *
 * Re-entering playback over the same window used to re-fetch every frame — a
 * signed-URL round trip plus an S3 GET per capture, for data already parsed.
 * Carrying the sensor, the archive length and the newest id means a refresh
 * taken in between still invalidates the cache.
 */
export function vrArchiveKey(sensor: any): string {
  const history = vrArchive(sensor);
  return [sensor?.id, history.length, history[history.length - 1]?.refreshId].join('|');
}

/**
 * The vessel features stored for one capture, or null when it cannot be read.
 *
 * Two hops by design: the route hands back a signed URL rather than the
 * payload, and the payload itself is fetched straight from S3. Each capture is
 * an already paid-for full-AOI pull, so replaying costs 0 credits.
 */
export async function vrLoadCapture(
  api: ApiService,
  http: HttpClient,
  scope: { sensorId: any; taskId: any },
  record: any,
): Promise<any[] | null> {
  try {
    const res: any = await api
      .getVoyageRadarSnapshot({
        sensorId: scope.sensorId,
        refreshId: record?.refreshId,
        taskid: scope.taskId,
        action: 'display',
      })
      .toPromise();
    const url = res?.result?.imageUrl;
    if (!url) return null;
    const json: any = await http.get(url).toPromise();
    return json?.features ?? json ?? [];
  } catch {
    return null;
  }
}
